import React from 'react';
import { motion } from 'framer-motion';
import {
  FaRocket,
  FaUsers,
  FaAward,
  FaGlobeAmericas,
  FaLightbulb,
  FaHandshake,
  FaBullseye, // Using for Mission
  FaEye // Using for Vision
} from 'react-icons/fa';
import './About.css';


// Data for the stats bar
const stats = [
  { icon: <FaRocket />, number: "50+", label: "Projects Launched" },
  { icon: <FaUsers />, number: "30+", label: "Happy Clients" },
  { icon: <FaAward />, number: "10+", label: "Awards Won" },
  { icon: <FaGlobeAmericas />, number: "10+", label: "Countries Served" }
];

// Data for the company timeline
const timelineEvents = [
  { year: "2025", event: "Company Founded", description: "DEPLOY-X was born from a vision to revolutionize the digital landscape with intelligent, scalable solutions." },
  { year: "2026", event: "AI Division Launched", description: "We officially launched our dedicated Artificial Intelligence division, pushing the boundaries of innovation." },
  { year: "2030", event: "We will hit Global Expansion", description: "Expanded our operations to serve clients across North America, Europe, and Asia." }
];

// Data for core values
const values = [
  { icon: <FaLightbulb />, title: "Innovation", description: "We push the boundaries of technology to deliver cutting-edge solutions." },
  { icon: <FaHandshake />, title: "Partnership", description: "We build long-term relationships, working together for mutual success." },
  { icon: <FaAward />, title: "Excellence", description: "Quality is at the heart of everything we do, from code to customer service." }
];

const About = () => {
  return (
    <section id="about">
      <div className="container">
        {/* Section Header */}
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Innovate. Create. <span>Deploy.</span></h2>
          <p className="section-subtitle">
            We are a team of passionate creators, thinkers, and builders dedicated to crafting the digital future. Our journey is defined by a relentless pursuit of excellence and a commitment to our clients' success.
          </p>
        </motion.div>

        {/* Two-Column Main Grid */}
        <div className="about-main-grid">
          {/* Left Column: Story & Timeline */}
          <motion.div
            className="about-story"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3>Our Story</h3>
            <p>Founded with a vision to revolutionize the digital landscape, DEPLOY-X has emerged as a leading force in AI-powered solutions. Our journey began with a simple belief: technology should empower businesses to achieve extraordinary results.</p>

            <h4 className="timeline-title">Our Journey</h4>
            <div className="timeline">
              {timelineEvents.map((item, index) => (
                <motion.div
                  className="timeline-item"
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="timeline-year">{item.year}</div>
                  <div className="timeline-content">
                    <h5>{item.event}</h5>
                    <p>{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column: Mission, Vision, Values */}
          <motion.div
            className="about-purpose"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="purpose-card mission-card">
              <div className="purpose-icon"><FaBullseye /></div>
              <h4>Our Mission</h4>
              <p>To empower businesses with innovative technology that drives growth, efficiency, and a competitive advantage in the digital age.</p>
            </div>
            <div className="purpose-card vision-card">
              <div className="purpose-icon"><FaEye /></div>
              <h4>Our Vision</h4>
              <p>To be a global leader in digital transformation, shaping the future of how businesses operate, innovate, and succeed.</p>
            </div>
          </motion.div>
        </div>
        {/* Values Section */}
        <motion.div className="about-values">
          <h4 className="values-title">Our Core Values</h4>
          <div className="values-grid">
            {values.map((value, index) => (
              <div className="value-card" key={index}>
                <div className="value-icon">{value.icon}</div>
                <div className="value-text">
                  <h5>{value.title}</h5>
                  <p>{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Full-Width Stats Bar */}
      <motion.div
        className="about-stats"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        viewport={{ once: true }}
      >
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div className="stat-card" key={index}>
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default About;